import React, { useState, useEffect, useMemo } from "react";
import "./Home.css";
import NavBar from "../components/Nav/navbar";
import Settings from "../components/Settings/Settings";
import Compendium from "../components/Compendium/Compendium";
import CompendiumView from "../components/CompendiumView/CompendiumView";
import AchievementsView from "../components/AchievementsView/AchievementsView";
import { IReducer } from "../state/reducer";
import BrickPack from "../components/Brickpack/brickpack";
import Sets from "../components/Sets/Sets";
import ActivityFeed from "../components/ActivityFeed/ActivityFeed";
import ActionSettings from "../components/ActionSettings/ActionSettings";
import InventorySettings from "../components/InventorySettings/InventorySettings";
import HelpModal from "../components/HelpModal/HelpModal";
import GlobalCanvasManager from "../components/GlobalCanvasManager/GlobalCanvasManager";
import { useCanvas } from "../components/GlobalCanvasManager/CanvasContext";
import Actions from "../components/Actions/Actions";
import Inventory from "../components/Inventory/Inventory";
import { HelpCircle, X } from "react-feather";

type ViewType = "main" | "compendium" | "achievements" | "settings";

interface HomeProps extends IReducer {
  onResetGame: () => void;
}

const HomeContent: React.FC<HomeProps> = ({ state, dispatch, onResetGame }) => {
  const [showActionSettings, setShowActionSettings] = useState(false);
  const [showInventorySettings, setShowInventorySettings] = useState(false);
  const [showCompenidum, setShowCompendium] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [currentView, setCurrentView] = useState<ViewType>("main");
  const [mobileNotificationDismissed, setMobileNotificationDismissed] =
    useState(() => {
      return localStorage.getItem("mobileNotificationDismissed") === "true";
    });
  const { syncViewports } = useCanvas();

  // Detect if user is on mobile device (user agent OR narrow screen)
  const [isMobile, setIsMobile] = useState(() => {
    const isMobileAgent =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    const isNarrowScreen = window.innerWidth < 768;
    return isMobileAgent || isNarrowScreen;
  });

  // Update mobile detection on window resize
  useEffect(() => {
    const handleResize = () => {
      const isMobileAgent =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const isNarrowScreen = window.innerWidth < 768;
      setIsMobile(isMobileAgent || isNarrowScreen);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dismissMobileNotification = () => {
    setMobileNotificationDismissed(true);
    localStorage.setItem("mobileNotificationDismissed", "true");
  };

  // Calculate visible brickpacks
  const visibleBrickpacks = useMemo(() => {
    const brickpacks: any[] = [];
    state.actions.forEach((action) => {
      if (action.visible && action.isBrickpack) {
        brickpacks.push(action);
      }
    });
    return brickpacks;
  }, [state.actions]);

  // Sync viewports when view changes to ensure models render immediately
  useEffect(() => {
    // Give React time to render the new view, then sync viewports
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncViewports();
      });
    });
  }, [currentView, syncViewports]);

  const switchView = (view: ViewType) => {
    setCurrentView(view);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        justifyContent: "flex-start",
        position: "relative",
        zIndex: 1,
      }}
    >
      {isMobile && (
        <div className="mobileNotification">
          <b className="mobileNotificationText">
            This game is best experienced on desktop and not designed for mobile
            devices
          </b>
        </div>
      )}
      <NavBar
        showCompendium={setShowCompendium}
        currentView={currentView}
        switchView={switchView}
      />
      {currentView === "main" && (
        <>
          <div className="coreContainer">
            <Actions
              state={state}
              dispatch={dispatch}
              showActionSettings={setShowActionSettings}
            />
            <Inventory
              state={state}
              dispatch={dispatch}
              showInventorySettings={setShowInventorySettings}
            />
            <div className="help-icon-container">
              {visibleBrickpacks.length > 0 ? (
                <HelpCircle
                  size={state.helpIconAnimated ? 42 : 32}
                  className={`help-icon ${state.helpIconAnimated ? "animated" : ""}`}
                  onClick={() => setShowHelpModal(true)}
                />
              ) : null}
            </div>
            <BrickPack state={state} dispatch={dispatch} />
            <Sets state={state} dispatch={dispatch} />
            <ActivityFeed state={state} dispatch={dispatch} />
          </div>
        </>
      )}
      {currentView === "compendium" && (
        <CompendiumView state={state} dispatch={dispatch} />
      )}
      {currentView === "achievements" && (
        <AchievementsView state={state} dispatch={dispatch} />
      )}
      {currentView === "settings" && (
        <Settings state={state} dispatch={dispatch} onResetGame={onResetGame} />
      )}
      <ActionSettings
        state={state}
        dispatch={dispatch}
        isOpen={showActionSettings}
        onClose={() => setShowActionSettings(false)}
      />
      <InventorySettings
        state={state}
        dispatch={dispatch}
        isOpen={showInventorySettings}
        onClose={() => setShowInventorySettings(false)}
      />
      <Compendium
        state={state}
        dispatch={dispatch}
        isOpen={showCompenidum}
        onClose={() => setShowCompendium(false)}
      />
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        state={state}
        dispatch={dispatch}
      />
    </div>
  );
};

const Home: React.FC<HomeProps> = ({ state, dispatch, onResetGame }) => {
  return (
    <GlobalCanvasManager>
      <HomeContent
        state={state}
        dispatch={dispatch}
        onResetGame={onResetGame}
      />
    </GlobalCanvasManager>
  );
};

export default Home;
